/*
Description: This is the file that will handle interacting with the HelloNote EMR
Date: 05/22/2025
Author: Kajol


TODO: 
    Make the template start with a SOAP note and fill it in with your template...


Notes:
    All Injectors need the following functions:
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion


/** Main Functions */
export function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "Evaluation Note",...
          }
    */
  const baseURL = window.location.origin;

  // Get the note type from the h3 element with class 'note-title'
  const headerEl = document.querySelector('#router-outlet-header .note-title');
  let noteType = headerEl ? headerEl.textContent.trim() : 'Default Note';

  // Remove date and patient name if present
  if (noteType) {
    // Example input:
    // •	[DRAFT NOTE] EVALUATION NOTE (06/19/2025) – GILLEY, KENNETH
    // •	DAILY NOTE (06/16/2025) – AMOS JR, KENNETH
    // •	[DRAFT NOTE] EVALUATION NOTE – CLARK, KENNETH
    const match = noteType.match(/(?:\[\w+\s+note\]\s*)?([a-z\s]+note)/i);
    if (match && match[1]) {
      noteType = match[1].trim(); // Gets "Daily Note" or "Evaluation Note"
    }
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  /*
      Description: This function will get the patient info from the note
      Return: A dictionary with the patient info
      {
          "EMR ID": "143038",
          "First Name": "Kenneth",
          "Last Name": "Clark",
          "DOB": "MM/DD/YYYY" or null
      }
    */

  // Support cases like:
  // - "Catherine Toth (260112661881, 06/23/1991)"
  // - "Kenneth Clark (143038)"

  // Prefer the actual span that holds the "First Last (id, dob)" string.
  // Avoid depending on Angular's `ng-star-inserted` class.
  const nameEl = document.querySelector('span.text-muted.fw-bold.me-4');
  if (!nameEl) {
    console.warn('Unable to extract patient name from the EMR');
    return null;
  }
  const nameText = (nameEl.textContent || '').trim();

  // Split "First Last (....)" into name + metadata (if present).
  const parenStart = nameText.lastIndexOf('(');
  const baseName = (
    parenStart === -1 ? nameText : nameText.slice(0, parenStart)
  ).trim();
  const insideParens =
    parenStart === -1
      ? ''
      : nameText
          .slice(parenStart + 1)
          .replace(/\)\s*$/, '')
          .trim(); // tolerate missing ')'

  // Name can be either "Last, First" or "First [Middle...] Last"
  let firstName = '';
  let lastName = '';
  if (baseName.includes(',')) {
    const [last, first] = baseName.split(',', 2);
    lastName = (last || '').trim();
    firstName = (first || '').trim();
  } else {
    const parts = baseName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      lastName = parts[parts.length - 1];
      firstName = parts.slice(0, parts.length - 1).join(' ');
    }
  }

  // Parse "(EMR_ID, DOB)" style metadata if present.
  let emrID: string | null = null;
  let dobFromHeader: string | null = null;
  if (insideParens) {
    const tokens = insideParens
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const idToken = tokens.find((t) => /\d{6,}/.test(t)) || tokens[0] || '';
    const idDigits = idToken.match(/\d{3,}/)?.[0] || null;
    if (idDigits) emrID = idDigits;

    const dobToken =
      tokens.find((t) => /\d{1,2}\/\d{1,2}\/\d{1,4}/.test(t)) || null;
    if (dobToken) {
      const m = dobToken.match(/\d{1,2}\/\d{1,2}\/\d{1,4}/);
      dobFromHeader = m ? m[0] : null;
    }
  }

  if (!firstName || !lastName) {
    console.warn('Unable to extract patient name from the EMR');
    return null;
  }

  if (!emrID) emrID = `${firstName}${lastName}`.replace(/\s+/g, '');
  const dob = getDOB() || dobFromHeader;
  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob || '05/29/2023'
  };
}

function getDOB() {
  const dobInput = document.querySelector<HTMLInputElement>(
    '#assessment_patientDOB'
  );
  return dobInput ? dobInput.value.trim() : null;
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
var questionInteractions = {};

export function getQuestions() {
  // Clear any previous state
  questions = [];
  questionInteractions = {};

  // Extract long answer questions from labeled <textarea> fields
  questions = extractLongAnswerQuestions();

  console.log('Extracted Long Answer Questions:', questions);
  return questions;
}

function extractLongAnswerQuestions() {
  const textareas = document.querySelectorAll('textarea');
  const extractedQuestions = [];

  textareas.forEach((textarea, index) => {
    const formGroup = textarea.closest('.form-group');

    if (formGroup) {
      const label = formGroup.querySelector('label');
      const labelText = label
        ? label.textContent.replace(/\*$/, '').trim()
        : null;
      if (!labelText) {
        return;
      }
      const key = labelText;

      const type = 'Long Answer';

      const existingNote = textarea.value || textarea.textContent.trim();

      extractedQuestions.push({
        Key: key,
        Question: labelText,
        Type: type,
        ExistingNote: existingNote
      });

      // Add to interactions map
      questionInteractions[labelText] = {
        Type: type,
        ExistingNote: existingNote,
        Input: (val, replyType) => {
          if (isEmptyOrNull(val)) {
            return;
          }
          const next =
            replyType === 'append'
              ? (textarea.value + '\n' + val).trim()
              : val;
          setTextareaValue(textarea, next);
        }
      };
    }
  });

  // Daily notes use p-editor (Quill) instead of textareas
  document
    .querySelectorAll<HTMLElement>('.form-group .p-editor-content .ql-editor')
    .forEach((editor) => {
      const label = editor.closest('.form-group').querySelector('label');
      const labelText = label?.textContent.replace(/\*\s*$/, '').trim();
      if (!labelText || questionInteractions[labelText]) return;

      const existingNote = quillText(editor);
      extractedQuestions.push({
        Key: labelText,
        Question: labelText,
        Type: 'Long Answer',
        ExistingNote: existingNote
      });
      questionInteractions[labelText] = {
        Type: 'Long Answer',
        ExistingNote: existingNote,
        Input: (val, replyType) => {
          if (isEmptyOrNull(val)) return;
          const next =
            replyType === 'append'
              ? (quillText(editor) + '\n' + val).trim()
              : val;
          setQuillValue(editor, next);
        }
      };
    });

  return extractedQuestions;
}

// innerText drops line breaks when the editor is hidden
function quillText(editor: HTMLElement) {
  return Array.from(editor.children).map((c) => c.textContent).join('\n').trim();
}

// Quill syncs DOM edits to its model via MutationObserver
function setQuillValue(editor: HTMLElement, value: string) {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  editor.innerHTML = value
    .split('\n')
    .map((l) => `<p>${l ? esc(l) : '<br>'}</p>`)
    .join('');
  editor.dispatchEvent(new Event('input', { bubbles: true }));
  editor.dispatchEvent(new Event('blur'));
}

// Angular forms only pick up values from input/change events
function setTextareaValue(textarea: HTMLTextAreaElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    'value'
  )?.set;
  if (setter) setter.call(textarea, value);
  else textarea.value = value;
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  textarea.dispatchEvent(new Event('change', { bubbles: true }));
  textarea.dispatchEvent(new Event('blur'));
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export function answerQuestions(answers) {
  /*
      Description: This function will take the answers and populate the questions
      
      Args: 
          answers: A dictionary of answers
              "Question": "The question in question"
              "Answer": "The answer to the question"
     
      Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
          [
              {
                  Question: "Where was the injury?", 
                  Answer: "Back" 
                  Error: "The error message" (Choice not found, etc)
              },
              ...
          }
  
      */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export function answerQuestion(question, answer, replyType) {
  /*
      Description: This function will take the answer and populate the question
      
      Args: 
          question: The question in question
          answer: The answer to the question
     
      Return: null if success otherwise a string with the error message
      */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

//  ---------------------------- ZOOM CONTROL ----------------------------
// HelloNote's top nav wraps under the fixed header at narrow widths (side panel
// open, iPad). Auto-fit zooms the EMR out until the nav is one row; -/+ sets a
// manual zoom (saved), clicking the % goes back to auto-fit.
const ZOOM_KEY = 'comprehendpt_hellonote_zoom';
const ZOOM_CONTROL_ID = 'comprehendpt-hellonote-zoom';
const NAV_SELECTOR = '.subheader ul.nav.nav-pills';
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.5;
const FIT_STEP = 0.05;
const FIT_MIN = 0.6;

let zoom = 1;

function readSavedZoom(): number | null {
  try {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY) || '');
    if (saved >= ZOOM_MIN && saved <= ZOOM_MAX) return saved;
  } catch (e) {
    // localStorage unavailable
  }
  return null;
}

function saveZoom(value: number | null) {
  try {
    if (value === null) localStorage.removeItem(ZOOM_KEY);
    else localStorage.setItem(ZOOM_KEY, String(value));
  } catch (e) {
    // localStorage unavailable
  }
}

function setRootZoom(value: number) {
  const root = document.documentElement;
  if (value === 1) root.style.removeProperty('zoom');
  else root.style.setProperty('zoom', String(value));
}

function navWrapped(nav: HTMLElement): boolean {
  const item = nav.firstElementChild as HTMLElement | null;
  return !!item && nav.offsetHeight > item.offsetHeight * 1.5;
}

// Reflows are synchronous, so only the final zoom gets painted.
function fitZoom(): number {
  const nav = document.querySelector(NAV_SELECTOR) as HTMLElement | null;
  let value = 1;
  setRootZoom(value);
  if (!nav) return value;
  while (navWrapped(nav) && value - FIT_STEP >= FIT_MIN) {
    value = Math.round((value - FIT_STEP) * 100) / 100;
    setRootZoom(value);
  }
  return value;
}

function applyZoom() {
  const saved = readSavedZoom();
  zoom = saved ?? fitZoom();
  setRootZoom(zoom);

  const control = document.getElementById(ZOOM_CONTROL_ID);
  if (!control) return;
  // Counter-zoom so the control stays the same size
  control.style.setProperty('zoom', String(1 / zoom));
  const label = control.querySelector('[data-zoom-label]') as HTMLElement | null;
  if (label) {
    label.textContent = `${Math.round(zoom * 100)}%`;
    label.title = saved === null ? 'Auto-fit' : 'Back to auto-fit';
  }
}

let fitScheduled = false;
function scheduleZoom() {
  if (fitScheduled) return;
  fitScheduled = true;
  requestAnimationFrame(() => {
    fitScheduled = false;
    applyZoom();
  });
}

function mountZoomControl() {
  if (document.getElementById(ZOOM_CONTROL_ID)) return;

  const control = document.createElement('div');
  control.id = ZOOM_CONTROL_ID;
  control.title = 'Comprehend: zoom EMR';
  control.style.cssText = `
    position: fixed; left: 12px; bottom: 12px; z-index: 2147483000;
    display: flex; align-items: center; gap: 2px; padding: 2px;
    background: #fff; border: 1px solid #d1d5db; border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15); opacity: 0.85;
    font: 500 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #374151; user-select: none;
  `;

  const setManualZoom = (next: number) => {
    saveZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 10) / 10)));
    applyZoom();
  };

  const makeButton = (text: string, ariaLabel: string, onClick: () => void) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = text;
    btn.setAttribute('aria-label', ariaLabel);
    btn.style.cssText = `
      min-width: 28px; height: 28px; padding: 0 6px; border: none;
      border-radius: 6px; background: transparent; color: inherit;
      font: inherit; font-size: 16px; line-height: 1; cursor: pointer;
    `;
    btn.addEventListener('click', onClick);
    return btn;
  };

  const label = makeButton('', 'Auto-fit EMR zoom', () => {
    saveZoom(null);
    applyZoom();
  });
  label.setAttribute('data-zoom-label', '');
  label.style.fontSize = '12px';

  control.append(
    makeButton('−', 'Zoom EMR out', () => setManualZoom(zoom - ZOOM_STEP)),
    label,
    makeButton('+', 'Zoom EMR in', () => setManualZoom(zoom + ZOOM_STEP))
  );
  document.body.appendChild(control);
  applyZoom();

  // Side panel open/close fires resize. The nav also re-wraps without a resize
  // (patient name loads, SPA route changes), so watch its size too.
  window.addEventListener('resize', scheduleZoom);
  const navResize = new ResizeObserver(scheduleZoom);
  let lastNav: Element | null = null;
  const watchNav = () => {
    const nav = document.querySelector(NAV_SELECTOR);
    if (nav === lastNav) return;
    if (lastNav) navResize.unobserve(lastNav);
    if (nav) navResize.observe(nav);
    lastNav = nav;
    scheduleZoom();
  };
  watchNav();
  new MutationObserver(watchNav).observe(document.body, { childList: true, subtree: true });
}

export function initHelloNoteZoom(): void {
  if (window.top !== window) return;
  const saved = readSavedZoom();
  if (saved !== null) setRootZoom(saved);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountZoomControl);
  } else {
    mountZoomControl();
  }
}
